package com.nonopn.skydome.common.app.checkout

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.CircularProgressIndicator
import androidx.compose.material.Surface
import androidx.compose.material.Text
import androidx.compose.material.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.checkout_return_activated
import com.nonopn.skywatcher.common.app.res.checkout_return_activating
import com.nonopn.skywatcher.common.app.res.checkout_return_cancel_text
import com.nonopn.skywatcher.common.app.res.checkout_return_cancel_title
import com.nonopn.skywatcher.common.app.res.checkout_return_close
import com.nonopn.skywatcher.common.app.res.checkout_return_delayed
import com.nonopn.skywatcher.common.app.res.checkout_return_login_needed
import com.nonopn.skywatcher.common.app.res.checkout_return_signing_in
import com.nonopn.skywatcher.common.app.res.checkout_return_success_title
import org.jetbrains.compose.resources.stringResource

/** What the banner shown after the return from the Stripe Checkout says. */
enum class CheckoutReturnPhase {
    /** checkout=success, the one time code is being exchanged for a session. */
    SigningIn,

    /** checkout=success but no session could be opened (InvalidLoginCode or any error): the user logs in. */
    LoginNeeded,

    /** Session open: GET /api/account/status is polled until the purchase appears. */
    Activating,
    Activated,

    /** Nothing appeared within the polling window. */
    ActivationDelayed,

    /** checkout=cancel: neutral message, no login attempt. */
    Cancelled
}

private val SuccessBackground = Color(0xFF1E5631)
private val NeutralBackground = Color(0xFF3A3F47)
private val BannerText = Color.White
private const val TitleSize = 16
private const val TextSize = 14

@Composable
fun CheckoutReturnBanner(
    phase: CheckoutReturnPhase,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier
) {
    val cancelled = phase == CheckoutReturnPhase.Cancelled
    // The payment is never presented as failed after checkout=success: only the second line changes
    val title = stringResource(
        if (cancelled) Res.string.checkout_return_cancel_title else Res.string.checkout_return_success_title
    )
    val text = stringResource(
        when (phase) {
            CheckoutReturnPhase.SigningIn -> Res.string.checkout_return_signing_in
            CheckoutReturnPhase.LoginNeeded -> Res.string.checkout_return_login_needed
            CheckoutReturnPhase.Activating -> Res.string.checkout_return_activating
            CheckoutReturnPhase.Activated -> Res.string.checkout_return_activated
            CheckoutReturnPhase.ActivationDelayed -> Res.string.checkout_return_delayed
            CheckoutReturnPhase.Cancelled -> Res.string.checkout_return_cancel_text
        }
    )
    val busy = phase == CheckoutReturnPhase.SigningIn || phase == CheckoutReturnPhase.Activating

    Surface(
        modifier = modifier.fillMaxWidth(),
        color = if (cancelled) NeutralBackground else SuccessBackground
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            if (busy) {
                CircularProgressIndicator(
                    modifier = Modifier.size(20.dp),
                    color = BannerText,
                    strokeWidth = 2.dp
                )
            }

            Column(modifier = Modifier.weight(1f)) {
                Text(title, color = BannerText, fontWeight = FontWeight.Bold, fontSize = TitleSize.sp)
                Text(text, color = BannerText, fontSize = TextSize.sp)
            }

            if (!busy) {
                TextButton(onClick = onDismiss) {
                    Text(stringResource(Res.string.checkout_return_close), color = BannerText)
                }
            }
        }
    }
}
